import { useTranslation } from 'react-i18next'
import type { SearchResultItem } from '../types'
import { IconImage, IconDownload, IconStar } from '@/app/components/icons'

interface SearchResultCardProps {
  readonly item: SearchResultItem
  readonly onClick: () => void
}

const TYPE_COLORS: Readonly<Record<string, string>> = {
  checkpoint: '#8b5cf6',
  lora: '#3b82f6',
  vae: '#10b981',
  controlnet: '#f59e0b',
  upscaler: '#ec4899',
  embedding: '#6366f1',
}

const PROVIDER_COLORS: Readonly<Record<string, string>> = {
  civitai: '#2563eb',
  huggingface: '#f59e0b',
}

function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return String(n)
}

export function SearchResultCard({ item, onClick }: SearchResultCardProps) {
  const { t } = useTranslation()

  const typeColor = TYPE_COLORS[item.resource_type] ?? '#6b7280'
  const providerColor = PROVIDER_COLORS[item.provider.toLowerCase()] ?? '#6b7280'
  const visibleTags = item.tags.slice(0, 5)

  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex flex-col gap-2 overflow-hidden rounded-xl border border-tg-hint/10 bg-tg-secondary text-left transition-all duration-200 active:scale-[0.97] hover:border-tg-hint/20 hover:shadow-lg"
    >
      {/* Preview image */}
      {item.preview_image_url !== null ? (
        <div className="relative h-32 w-full overflow-hidden bg-tg-secondary">
          <div className="absolute inset-0 bg-gradient-to-t from-tg-bg/20 to-transparent" />
          <img
            src={item.preview_image_url}
            alt={item.name}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
          />
        </div>
      ) : (
        <div
          className="flex h-32 w-full items-center justify-center"
          style={{ backgroundColor: `color-mix(in srgb, ${typeColor} 12%, var(--tg-theme-secondary-bg-color))` }}
        >
          <IconImage className="h-8 w-8" style={{ color: `color-mix(in srgb, ${typeColor} 50%, var(--tg-theme-hint-color))` }} />
        </div>
      )}

      <div className="flex flex-col gap-2 px-3 pb-3">
        {/* Name */}
        <span
          className="line-clamp-2 text-sm font-medium leading-tight text-tg-text"
        >
          {item.name}
        </span>

        {/* Author */}
        <span
          className="truncate text-[11px] text-tg-hint"
        >
          {t('search.by')} {item.author}
        </span>

        {/* Badges */}
        <div className="flex flex-wrap gap-1">
          <span
            className="rounded-full px-2 py-0.5 text-[10px] font-medium"
            style={{ backgroundColor: `${typeColor}20`, color: typeColor }}
          >
            {t(`resources.filter.${item.resource_type}`)}
          </span>
          <span
            className="rounded-full px-2 py-0.5 text-[10px] font-medium"
            style={{ backgroundColor: `${providerColor}20`, color: providerColor }}
          >
            {item.provider}
          </span>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <IconDownload className="h-2.5 w-2.5 text-tg-hint" />
            <span className="text-[10px] text-tg-hint">
              {formatCount(item.download_count)}
            </span>
          </div>
          {item.rating > 0 && (
            <div className="flex items-center gap-1">
              <IconStar className="h-2.5 w-2.5" style={{ color: 'var(--tg-theme-button-color)' }} />
              <span className="text-[10px] text-tg-hint">
                {item.rating.toFixed(1)}
              </span>
            </div>
          )}
        </div>

        {/* Tags */}
        {visibleTags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {visibleTags.map((tag) => (
              <span
                key={tag}
                className="rounded bg-tg-bg px-1.5 py-0.5 text-[10px] text-tg-hint"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </button>
  )
}
